import { authFetch, BASE_BACKEND_URL } from '../auth'
import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import cookie from 'react-cookies'
import NavBar from '../layout/NavBar'
import TopBar from '../layout/TopBar'
import Select from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import TemporaryAccessSuccessModal from './component/modal/TemporaryAccessSuccessModal'
import { TEMPORARY_ACCESS_TEXT } from './constants/temporaryAccessConstants'
import { sanitizeUID } from '../helpers/utils'
import { LayoutContext } from '../layout/MainLayout'

const CreateUsersForm = () => {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [temporaryAccess, setTemporaryAccess] = useState(null)
  const [readyToCollect, setReadyToCollect] = useState(false)
  const [failedCreation, setFailedCreation] = useState(false)
  const [currentUidsData, setCurrentUidsData] = useState(null)
  const [featureRoles, setFeatureRoles] = useState([])
  const [warehouses, setWarehouses] = useState([])

  // User form values
  const [active, setActive] = useState(true)
  const [name, setName] = useState(null)
  const [nameError, setNameError] = useState(null)
  const [isSupervisor, setIsSupervisor] = useState(false)
  const [roles, setRoles] = useState([])
  const [rolesError, setRolesError] = useState(null)
  const [uid, setUid] = useState(null)
  const [internalCode, setInternalCode] = useState('')
  const [uidError, setUidError] = useState(null)
  const [email, setEmail] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const [warehouseUid, setWarehouseUid] = useState(null)
  const [phoneNum, setPhoneNum] = useState(null)

  ////// Stops fetchs when page is changed
  const abortController = new AbortController()
  const abortCurrentFetchs = () => {
    abortController.abort('Fetch Aborted by User.')
  }
  window.addEventListener('beforeunload', abortCurrentFetchs)
  //////

  const sendForm = (event) => {
    event.preventDefault()
    setFailedCreation(false)
    setReadyToCollect(true)
  }

  const getFeatureRoles = (query = '', page = 1, per_page = 100) => {
    try {
      return authFetch(
        `${BASE_BACKEND_URL}/ubiqua_app_feature_roles?q=${query}&page=${page}&per_page=${per_page}`,
        {
          method: 'GET',
          signal: abortController.signal,
        }
      )
        .then((response) => {
        if (!response) return
        return response.json()
      })
        .then((data) => {
          setFeatureRoles(
            data?.table
              ?.filter((role) => role.key !== 'role.supervisor')
              .map((role) => ({ value: role.key, role: role.name, label: role.name }))
          )
        })
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    }
  }

  const getWarehouses = () => {
    try {
      return authFetch(`${BASE_BACKEND_URL}/warehouses`, {
        method: 'GET',
        signal: abortController.signal,
      })
        .then((response) => {
        if (!response) return
        return response.json()
      })
        .then((data) => {
          setWarehouses(
            data?.result?.map((warehouse) => ({
              value: warehouse.uid,
              label: `${warehouse.uid} - ${warehouse.name}`
            }))
          )
        })
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    }
  }

  const getUsersUids = () => {
    authFetch(`${BASE_BACKEND_URL}/ubiqua_app_users?non_paginated=true`, {
      method: 'GET',
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setCurrentUidsData(data.table.map((user) => user.uid))
      })
      .catch((e) => console.log(e))
  }

  const postFormToBackend = (body) => {
    authFetch(
      `${BASE_BACKEND_URL}/ubiqua_app_users`,
      {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify(body)
      }
    )
      .then((r) => evaluateResponse(r))
      .catch((error) => {
        if (error !== 'Fetch Aborted by User.') {
          console.error('Error:', error)
        }
      })
  }

  const evaluateResponse = async (response) => {
    // navigate to task details
    switch (response.status) {
      case 422:
        setFailedCreation('Error al crear usuario nuevo. Consulte a soporte.')
        setSaving(false)
        break
      case 409:
        setFailedCreation('Este email ya está en uso. Por favor escoja otro.')
        setSaving(false)
        break
      case 404:
      case 400:
        setFailedCreation('Servidor fuera de linea. Intente más tarde')
        setSaving(false)
        break
      case 500:
        setFailedCreation('Error interno del sistema. Intente más tarde')
        setSaving(false)
        break
      default:
        let data = await response.json()
        setFailedCreation(false)
        if (!response.ok || !data.temporary_password) {
          setFailedCreation(data.error || data.message || 'No se pudo crear el acceso temporal.')
          setSaving(false)
          break
        }
        setTemporaryAccess(data)
        setSaving(false)
        break
    }
  }

  const cancelCreate = (event) => {
    event.preventDefault()
    const val = window.confirm(
      '¿Esta seguro que quiere continuar? Los cambios editados no seran guardados'
    )
    if (val) {
      navigate('/usuarios/')
    }
  }

  const handleStatusChange = (event) => {
    setActive(!active)
  }

  const handleNameChange = (event) => {
    setName(event.target.value)
  }

  const handleSupervisorChange = () => {
    setIsSupervisor(!isSupervisor)
  }

  const handleRolesChange = (event) => {
    if (event) {
      setRoles(event)
    } else {
      setRoles([])
    }
  }

  const handleUidChange = (event) => {
    setUid(sanitizeUID(event.target.value))
  }

  const handleEmailChange = (event) => {
    setEmail(event.target.value)
  }

  const validateEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
    return emailRegex.test(email)
  }

  const handleWarehouseChange = (event) => {
    if (event) {
      setWarehouseUid(event.value)
    } else {
      setWarehouseUid(null)
    }
  }

  const handlePhoneChange = (event) => {
    setPhoneNum(event.target.value)
  }

  const validateData = () => {
    setSaving(true)
    let checkIsGood = true
    if (name == null) {
      setNameError('Este es un campo obligatorio.')
      checkIsGood = false
    }
    if (roles.length == 0 && !isSupervisor) {
      setRolesError('Este es un campo obligatorio.')
      checkIsGood = false
    }
    if (uid == null) {
      setUidError('Este es un campo obligatorio.')
      checkIsGood = false
    }
    if (uid !== null && currentUidsData.includes(uid)) {
      setUidError('Este número de rutero ya está en uso. Por favor escoja otro. ')
      checkIsGood = false
    }
    if (email == null) {
      setEmailError('Este es un campo obligatorio.')
      checkIsGood = false
    }
    if (email !== null && !validateEmail(email)) {
      setEmailError('El formato no es válido.')
      checkIsGood = false
    }

    if (checkIsGood) {
      setNameError(null)
      setRolesError(null)
      setUidError(null)
      setEmailError(null)
      formDataCollector()
    } else {
      setReadyToCollect(false)
      return setSaving(false)
    }
  }

  const formDataCollector = () => {
    let jsonData = {
      active: active,
      full_name: name,
      supervisor: isSupervisor,
      roles: roles.map((r) => r.value),
      email: email,
      uid: uid,
      route_db_ref: internalCode.trim() || null,
      warehouse_uid: warehouseUid,
      mobile: phoneNum
    }
    postFormToBackend(jsonData)
    setReadyToCollect(false)
  }

  useEffect(() => {
    getFeatureRoles()
    getWarehouses()
    getUsersUids()
  }, [])

  useEffect(() => {
    if (isSupervisor) {
      setRoles([])
    }
  }, [isSupervisor])

  useEffect(() => {
    if (readyToCollect) {
      setNameError(null)
      setRolesError(null)
      setUidError(null)
      setEmailError(null)
      validateData()
    }
  }, [readyToCollect])

  return (
    <div>
      <TemporaryAccessSuccessModal
        access={temporaryAccess}
        onAccessClose={() => setTemporaryAccess(null)}
        onComplete={(createdUid) => {
          navigate(`/usuarios/${encodeURIComponent(createdUid)}`, { replace: true })
        }}
      />
      <div>
        <div>
          <>
            {failedCreation && (
              <div className="alert alert-danger" role="alert">
                <strong>{failedCreation}</strong>
              </div>
            )}

            <div className="show-title">
              <h2>Crear usuario</h2>
            </div>
            <form onSubmit={sendForm}>
              <div className="index-table">
                <div className="show-area">
                  <div className="create-promo-wrapper">
                    <div className="inline-block full-width">
                      <div className="float-left">
                        <h5>Ingrese los siguientes datos para crear un usuario nuevo</h5>
                      </div>
                      <div className="float-right">
                        <button disabled={saving} onClick={cancelCreate} className="cancel-button">
                          Cancelar
                        </button>
                        <input
                          disabled={saving}
                          className="save-button"
                          value={saving ? 'Guardando...' : 'Guardar'}
                          type="submit"
                        />
                      </div>
                    </div>
                    <hr />
                    <div className="mb-10">
                      Por favor, complete los campos obligatorios marcados con{' '}
                      <span className="red">*</span> para proceder
                    </div>
                    <div className="promo-form-section">
                      <div className="form-group row">
                        <label className="col-sm-3 col-form-label">Activo:</label>
                        <div className="col-sm-8">
                          <select
                            name="active"
                            className={`form-control custom-select`}
                            onChange={(e) => handleStatusChange(e)}
                          >
                            <option key="si" value={true}>
                              Si
                            </option>
                            <option key="no" value={false}>
                              No
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputName" className="col-sm-3 col-form-label">
                          Nombre de usuario<span className="red">*</span>:
                        </label>
                        <div className="col-sm-8">
                          <input
                            name="name"
                            type="text"
                            className={`form-control ${nameError ? 'is-invalid' : ''}`}
                            placeholder="Nombre de usuario"
                            onChange={(e) => handleNameChange(e)}
                            required
                          />
                          <div className="invalid-feedback">{nameError}</div>
                        </div>
                      </div>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputName" className="col-sm-3 col-form-label">
                          Rol de supervisor:
                        </label>
                        <div className="col-sm-8 flex-align-center">
                          <span className="supervisor-radio-span">
                            <input
                              type="radio"
                              className="supervisor-radio-option"
                              checked={isSupervisor == true}
                              onChange={handleSupervisorChange}
                            />
                            Si
                          </span>
                          <span className="supervisor-radio-span">
                            <input
                              type="radio"
                              className="supervisor-radio-option"
                              checked={isSupervisor == false}
                              onChange={handleSupervisorChange}
                            />
                            No
                          </span>
                        </div>
                      </div>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                          Rol de usuario<span className="red">*</span>:
                        </label>
                        <div className="col-sm-8">
                          <Select
                            placeholder={'Buscar o asignar multiples roles'}
                            noOptionsMessage={() => 'Buscar'}
                            loadingMessage={() => 'Cargando...'}
                            isDisabled={isSupervisor}
                            isClearable={true}
                            value={roles}
                            options={featureRoles}
                            isMulti
                            onChange={(e) => handleRolesChange(e)}
                          />
                          <div className="invalid-feedback" style={{ display: 'block' }}>
                            {rolesError}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                          Número de rutero/Código<span className="red">*</span>:
                        </label>
                        <div className="col-sm-8">
                          <input
                            name="code"
                            type="text"
                            className={`form-control ${uidError ? 'is-invalid' : ''}`}
                            placeholder="123"
                            onChange={(e) => handleUidChange(e)}
                            value={uid}
                            required
                          />
                          <div className="invalid-feedback">{uidError}</div>
                        </div>
                      </div>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputInternalCode" className="col-sm-3 col-form-label">
                          Código interno:
                        </label>
                        <div className="col-sm-8">
                          <input
                            id="inputInternalCode"
                            name="route_db_ref"
                            type="text"
                            className="form-control"
                            value={internalCode}
                            onChange={(event) => setInternalCode(event.target.value)}
                          />
                        </div>
                      </div>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                          Correo electrónico<span className="red">*</span>:
                        </label>
                        <div className="col-sm-8">
                          <input
                            name="email"
                            type="email"
                            className={`form-control ${emailError ? 'is-invalid' : ''}`}
                            placeholder="ejemplo@email.com"
                            onChange={(e) => handleEmailChange(e)}
                            required
                          />
                          <div className="invalid-feedback">{emailError}</div>
                        </div>
                      </div>

                      <p>{TEMPORARY_ACCESS_TEXT.createNotice}</p>

                      <div className="form-group row show-data-form">
                        <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                          Código de bodega:
                        </label>
                        <div className="col-sm-8">
                          <Select
                            placeholder={'Buscar o asignar bodega'}
                            noOptionsMessage={() => 'Buscar'}
                            loadingMessage={() => 'Cargando...'}
                            isDisabled={false}
                            isClearable={true}
                            onChange={(e) => handleWarehouseChange(e)}
                            options={warehouses}
                          />
                        </div>
                      </div>

                      <div className="form-group row ">
                        <label htmlFor="inputCode" className="col-sm-3 col-form-label">
                          Número de teléfono:
                        </label>
                        <div className="col-sm-8">
                          <input
                            name="mobile"
                            type="text"
                            className={`form-control`}
                            placeholder="0000-0000"
                            onChange={(e) => handlePhoneChange(e)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </>
        </div>
      </div>
    </div>
  )
}
export default CreateUsersForm
